import { format, isValid, parse } from 'date-fns';
import { ICriteria } from '../../contracts/criteria.contract';

const CRITERIA_DATE_KEYS = [
    'EntryIntoForceBeforeDate',
    'EntryIntoForceAfterDate',
    'ContractBeforeDate',
    'ContractAfterDate',
    'KeelLayBeforeDate',
    'KeelLayAfterDate',
    'DeliveryBeforeDate',
    'DeliveryAfterDate',
    'ExpiryBeforeDate',
    'ExpiryAfterDate'
] as const;

/** Local calendar day as yyyy-MM-dd (avoids UTC day-shift from JSON Date serialization). */
export const toApiDateString = (value: Date | string | null | undefined): string | null => {
    if (value == null || value === '') return null;
    const date = value instanceof Date ? value : new Date(value);
    if (!isValid(date)) return null;
    return format(date, 'yyyy-MM-dd');
};

/** Comparable number for the local calendar day (year/month/day only). */
export const toCalendarDayValue = (value: Date | string | null | undefined): number | null => {
    if (value == null || value === '') return null;

    if (typeof value === 'string') {
        const trimmed = value.trim();
        // Tracker API formats ShipAgeDate as dd-MMM-yyyy (e.g. 01-Jan-2028)
        const fromApiFormat = parse(trimmed, 'dd-MMM-yyyy', new Date());
        if (isValid(fromApiFormat)) {
            return Date.UTC(fromApiFormat.getFullYear(), fromApiFormat.getMonth(), fromApiFormat.getDate());
        }
    }

    const date = value instanceof Date ? value : new Date(value);
    if (!isValid(date)) return null;
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
};

/** Inclusive: user calendar day is on or after the amendment ShipAgeDate calendar day. */
export const isUserDateOnOrAfterShipAge = (userDate: Date | string | null | undefined, shipAgeDate: string | null | undefined): boolean => {
    const userDay = toCalendarDayValue(userDate);
    const shipDay = toCalendarDayValue(shipAgeDate);
    if (userDay === null || shipDay === null) return false;
    return userDay >= shipDay;
};

/** Clone criteria with date fields as local yyyy-MM-dd strings for the FilterCriteria API. */
export const serializeCriteriaDatesForApi = (criteria: ICriteria): Record<string, unknown> => {
    const payload: Record<string, unknown> = { ...criteria };
    for (const key of CRITERIA_DATE_KEYS) {
        payload[key] = toApiDateString(criteria[key]);
    }
    return payload;
};
